import { StatusCode } from "services/api/flagbags/types";
import { initIndexedDbIfMissingStore } from "utils/indexedDbRecovery";
import { v7 as uuidv7 } from "uuid";
import { useData } from "store/DataProvider";

export interface FlagbagLogEntry {
  id?: string;
  flagbagId: string;
  volunteerId?: string;
  stationId: string;
  status: StatusCode;
  timestamp: number;
  createdAt?: Date;
}

const useFlagBagLogController = () => {
  const data = useData();
  const logDB = initIndexedDbIfMissingStore("flagbaglog");

  const logStatusUpdate = async (
    flagbagId: string,
    status: StatusCode,
    stationId?: string,
    logId?: string,
  ): Promise<void> => {
    try {
      const currentStationId = stationId || data.getStationId();
      if (!currentStationId) {
        console.warn("FlagbagLogController: No station ID available, skipping log");
        return;
      }

      const logEntry: FlagbagLogEntry = {
        id: logId ?? uuidv7(),
        flagbagId,
        stationId: currentStationId,
        status,
        timestamp: Date.now(),
        createdAt: new Date(),
      };

      await logDB.add(logEntry);
    } catch (error) {
      console.error("FlagbagLogController: Failed to log status update:", error);
    }
  };

  const getLogsByFlagbagId = async (flagbagId: string): Promise<FlagbagLogEntry[]> => {
    try {
      const allLogs = await logDB.getAll();
      return allLogs.filter((log: FlagbagLogEntry) => log.flagbagId === flagbagId);
    } catch (error) {
      console.error("FlagbagLogController: Failed to get logs by flagbag ID:", error);
      return [];
    }
  };

  const getLogsByStationId = async (stationId: string): Promise<FlagbagLogEntry[]> => {
    try {
      const allLogs = await logDB.getAll();
      return allLogs.filter((log: FlagbagLogEntry) => log.stationId === stationId);
    } catch (error) {
      console.error("FlagbagLogController: Failed to get logs by station ID:", error);
      return [];
    }
  };

  const getAllLogs = async (): Promise<FlagbagLogEntry[]> => {
    try {
      return await logDB.getAll();
    } catch (error) {
      console.error("FlagbagLogController: Failed to get all logs:", error);
      return [];
    }
  };

  const clearAllLogs = async (): Promise<void> => {
    try {
      await logDB.clear();
    } catch (error) {
      console.error("FlagbagLogController: Failed to clear logs:", error);
    }
  };

  const deleteLogsByFlagbagId = async (flagbagId: string): Promise<void> => {
    try {
      const logs = await getLogsByFlagbagId(flagbagId);
      for (const log of logs) {
        if (log.id) {
          await logDB.deleteRecord(log.id);
        }
      }
    } catch (error) {
      console.error("FlagbagLogController: Failed to delete logs by flagbag ID:", error);
    }
  };

  return {
    logStatusUpdate,
    getLogsByFlagbagId,
    getLogsByStationId,
    getAllLogs,
    clearAllLogs,
    deleteLogsByFlagbagId,
  };
};

export default useFlagBagLogController;
